import {useState, useEffect, useCallback} from 'react';
import {usePermissionStore} from '../store/permissionStore';
import {Permission} from '../models/permission.model';

interface DynamicPermissionData {
  permissions: Permission[];
  resources: string[];
  actions: string[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to dynamically fetch and manage permissions from backend
 * This replaces hardcoded permission constants with dynamic API data
 */
export const useDynamicPermissions = () => {
  const [permissionData, setPermissionData] = useState<DynamicPermissionData>({
    permissions: [],
    resources: [],
    actions: [],
    loading: false,
    error: null
  });

  const {fetchAllPermissions, reset} = usePermissionStore();

  // Fetch all permissions from backend
  const loadPermissions = useCallback(async () => {
    setPermissionData(prev => ({...prev, loading: true, error: null}));

    try {
      const result = await fetchAllPermissions();

      if (result && result.permissions) {
        const permissions = result.permissions;

        // Extract unique resources and actions
        const resources = [...new Set(permissions.map(p => p.resource))].sort();
        const actions = [...new Set(permissions.map(p => p.action))].sort();

        setPermissionData({
          permissions,
          resources,
          actions,
          loading: false,
          error: null
        });
      } else {
        setPermissionData(prev => ({
          ...prev,
          loading: false,
          error: 'No permissions returned from backend'
        }));
      }
    } catch (error) {
      setPermissionData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load permissions'
      }));
    }
  }, [fetchAllPermissions]);

  // Load permissions on mount
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Get permissions by resource
  const getPermissionsByResource = useCallback((resource: string): Permission[] => {
    return permissionData.permissions.filter(p => p.resource === resource);
  }, [permissionData.permissions]);

  // Get permissions by action
  const getPermissionsByAction = useCallback((action: string): Permission[] => {
    return permissionData.permissions.filter(p => p.action === action);
  }, [permissionData.permissions]);

  // Get resource display name (with fallback)
  const getResourceDisplayName = useCallback((resource: string): string => {
    const resourceMap: Record<string, string> = {
      'users': 'المستخدمين',
      'roles': 'الأدوار',
      'permissions': 'الصلاحيات',
      'products': 'المنتجات',
      'stores': 'المتاجر',
      'visits': 'الزيارات',
      'billing': 'الفواتير',
      'sales': 'المبيعات',
      'inventory': 'المخزون',
      'brands': 'البراندات',
      'dashboard': 'لوحة التحكم',
      'mobile': 'التطبيق المحمول',
      'system': 'النظام',
      'reports': 'التقارير',
      'settings': 'الإعدادات'
    };
    return resourceMap[resource] || resource;
  }, []);

  // Get action display name (with fallback)
  const getActionDisplayName = useCallback((action: string): string => {
    const actionMap: Record<string, string> = {
      // Core CRUD
      'create': 'إنشاء',
      'read': 'عرض',
      'update': 'تعديل',
      'delete': 'حذف',

      // Management
      'manage': 'إدارة',
      'assign': 'تعيين',

      // Business Actions
      'approve': 'موافقة',
      'export': 'تصدير',
      'transfer': 'النقل',
      'collect_payment': 'تحصيل الدفع',

      // System Actions
      'access': 'الوصول',
      'analytics': 'التحليلات',
      'reports': 'التقارير',
      'settings': 'الإعدادات',
      'logs': 'السجلات',
      'backup': 'النسخ الاحتياطي',

      // Domain Specific
      'sales': 'المبيعات',
      'visits': 'الزيارات',

      // Legacy
      'list': 'قائمة',
      'import': 'استيراد',
      'reject': 'رفض',
      'view': 'عرض'
    };
    return actionMap[action] || action;
  }, []);

  // Group permissions by resource
  const getGroupedPermissions = useCallback(() => {
    const grouped: Record<string, Permission[]> = {};

    permissionData.permissions.forEach(permission => {
      if (!grouped[permission.resource]) {
        grouped[permission.resource] = [];
      }
      grouped[permission.resource].push(permission);
    });

    return grouped;
  }, [permissionData.permissions]);

  // Refresh permissions - NO CACHING
  const refreshPermissions = useCallback(() => {
    // Clear the store cache to ensure fresh data
    reset();
    // Load fresh permissions directly
    loadPermissions();
  }, [loadPermissions, reset]);

  return {
    // Data
    permissions: permissionData.permissions,
    resources: permissionData.resources,
    actions: permissionData.actions,
    groupedPermissions: getGroupedPermissions(),

    // Loading states
    loading: permissionData.loading,
    error: permissionData.error,

    // Utility functions
    getPermissionsByResource,
    getPermissionsByAction,
    getResourceDisplayName,
    getActionDisplayName,
    refreshPermissions
  };
};
